// DangerZonePanel — May 29, 2026.
//
// Two clearly-labeled destructive admin buttons:
//   1. Nuke all data → wipes everyone except founder + founder's silos.
//   2. Seed 6 test personas → idempotent test-account seeder.
// Each has its own typed-confirmation modal so they can't be hit by accident.
import { useState } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import { toast } from "sonner";
import {
  Warning, FireSimple, Sparkle, Spinner, X as XIcon, CheckCircle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";

export default function DangerZonePanel() {
  // Feb 2026 (Randy pt-batch5c-dynamic-admin) — the "residuals" copy on
  // the seed-personas cards used to hardcode $10/mo and $300 — went
  // stale the moment Randy moved the sliders. Now reads /public/pricing.
  const pricing = usePublicPricing();
  const monthly = fmt$(pricing.platform_fee_cents);
  const gate = fmt$(pricing.day_one_bundle_discount_threshold_cents);
  const [nukeOpen, setNukeOpen] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [randomOpen, setRandomOpen] = useState(false);
  const [stackOpen, setStackOpen] = useState(false);
  const [randomCount, setRandomCount] = useState(6);
  const [stackCount, setStackCount] = useState(6);
  const [stackRealOnly, setStackRealOnly] = useState(false);
  const [populateUsage, setPopulateUsage] = useState(true);
  const [stripeBackedStandard, setStripeBackedStandard] = useState(false);
  const [stripeBackedRandom, setStripeBackedRandom] = useState(false);
  const [stripeBackedStack, setStripeBackedStack] = useState(false);
  const [deepClean, setDeepClean] = useState(false);
  const [refundStripeSandbox, setRefundStripeSandbox] = useState(false);
  const [lastNuke, setLastNuke] = useState(null);
  const [lastSeed, setLastSeed] = useState(null);
  const [lastRandom, setLastRandom] = useState(null);
  const [lastStack, setLastStack] = useState(null);

  return (
    <section
      className="mt-10 pt-8 border-t-2 border-[#C16E5A] space-y-6 siloart-keep"
      data-testid="danger-zone-panel"
    >
      <div className="flex items-baseline gap-3">
        <h2 className="font-serif-display text-2xl text-[#7A3A2A]">⚠️ Danger Zone</h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#7A3A2A]">
          irreversible
        </span>
      </div>
      <p className="text-xs text-[#6B6A68] leading-relaxed max-w-2xl">
        These buttons act on whatever environment you're currently in (preview or prod).
        The nuke preserves YOUR founder account, your silos, your BYOK keys, your YouTube data,
        and your battle history. Everything else is wiped. The seed creates 6 test personas
        for end-to-end testing.
      </p>

      {/* Global toggle for usage history auto-population */}
      <label className="inline-flex items-center gap-2 text-[11px] text-[#1C1B1A] bg-white border hairline rounded-md px-3 py-1.5 cursor-pointer" data-testid="populate-usage-toggle">
        <input
          type="checkbox"
          checked={populateUsage}
          onChange={(e) => setPopulateUsage(e.target.checked)}
          className="w-3.5 h-3.5"
        />
        <span>Pre-populate random LLM usage history (5-50 logs per persona)</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Nuke button */}
        <div className="bg-[#FDFCF8] border-2 border-[#C16E5A] rounded-lg p-4" data-testid="nuke-card">
          <div className="flex items-start gap-2 mb-2">
            <FireSimple size={20} weight="duotone" className="text-[#C16E5A] shrink-0 mt-0.5" />
            <div>
              <div className="font-serif-display text-base text-[#1C1B1A]">Nuke all data</div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                Wipes every user, silo, usage log, payment txn, voucher balance, and reservoir.
                <strong className="block text-[#1C1B1A] mt-1">Preserves:</strong>
                your founder account · your silos · BYOK keys · YouTube data · battle history
              </div>
            </div>
          </div>
          <button
            onClick={() => setNukeOpen(true)}
            data-testid="nuke-open-btn"
            className="mt-2 text-xs px-3 py-2 rounded bg-[#C16E5A] text-white hover:bg-[#A35947] flex items-center gap-1.5 font-medium"
          >
            <FireSimple size={12} weight="fill" /> Initiate Nuke
          </button>
          {lastNuke && (
            <div className="mt-3 text-[10px] bg-white border hairline rounded p-2">
              <div className="flex items-center gap-1 text-[#2F6B3A]"><CheckCircle size={10} weight="duotone" /> Last nuke:</div>
              <div className="text-[#6B6A68] mt-0.5">
                Wiped {lastNuke.wiped?.user_ids_count} users · {lastNuke.wiped?.silo_ids_count} silos
                at {new Date(lastNuke.finished_at).toLocaleTimeString()}
              </div>
            </div>
          )}
        </div>

        {/* Seed button */}
        <div className="bg-[#FDFCF8] border-2 border-[#4A5D4E] rounded-lg p-4" data-testid="seed-card">
          <div className="flex items-start gap-2 mb-2">
            <Sparkle size={20} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div>
              <div className="font-serif-display text-base text-[#1C1B1A]">Seed 6 test personas</div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                Creates fan + paidfan + red creator ($5 voucher) + green creator (cloned BYOK) +
                mixed creator + affiliate creator. Each with a starter silo + doc. Idempotent
                (purges prior seeds, then creates).
              </div>
            </div>
          </div>
          <button
            onClick={() => setSeedOpen(true)}
            data-testid="seed-open-btn"
            className="mt-2 text-xs px-3 py-2 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1.5 font-medium"
          >
            <Sparkle size={12} weight="fill" /> Seed test personas
          </button>
          {lastSeed && (
            <div className="mt-3 text-[10px] bg-white border hairline rounded p-2">
              <div className="flex items-center gap-1 text-[#2F6B3A]"><CheckCircle size={10} weight="duotone" /> Last seed:</div>
              <div className="text-[#6B6A68] mt-0.5">
                Created {lastSeed.created?.length} personas · password: <code>{lastSeed.password_for_all}</code>
              </div>
            </div>
          )}
        </div>

        {/* Random persona seeder */}
        <div className="bg-[#FDFCF8] border-2 border-[#7B7AA8] rounded-lg p-4" data-testid="random-card">
          <div className="flex items-start gap-2 mb-2">
            <Sparkle size={20} weight="duotone" className="text-[#7B7AA8] shrink-0 mt-0.5" />
            <div>
              <div className="font-serif-display text-base text-[#1C1B1A]">🎲 Random personas</div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                Generates N <strong>fully-randomized</strong> users — varied roles, BYOK loadouts,
                voucher levels, silo counts, referral chains, payment states, and account ages.
                Each run produces a different set. Catches edge cases the deterministic seed misses.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <label className="text-[11px] text-[#6B6A68]">Count:</label>
            <FreeNumberInput
              min={1} max={20}
              value={randomCount}
              onChange={(v) => setRandomCount(v)}
              data-testid="random-count-input"
              className="w-14 px-2 py-1 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#7B7AA8] tabular-nums"
            />
          </div>
          <button
            onClick={() => setRandomOpen(true)}
            data-testid="random-open-btn"
            className="mt-2 text-xs px-3 py-2 rounded bg-[#7B7AA8] text-white hover:bg-[#5C5B85] flex items-center gap-1.5 font-medium"
          >
            <Sparkle size={12} weight="fill" /> Roll the dice
          </button>
          {lastRandom && (
            <div className="mt-3 text-[10px] bg-white border hairline rounded p-2">
              <div className="flex items-center gap-1 text-[#2F6B3A]"><CheckCircle size={10} weight="duotone" /> Last random run:</div>
              <div className="text-[#6B6A68] mt-0.5">
                {lastRandom.count} personas · email domain: <code>{lastRandom.email_domain}</code> · pw: <code>{lastRandom.password_for_all}</code>
              </div>
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[9px] text-[#7B7AA8] hover:underline">Show roster</summary>
                <ul className="mt-1 space-y-0.5 text-[9px] font-mono text-[#1C1B1A] max-h-32 overflow-y-auto">
                  {lastRandom.personas?.map((p) => (
                    <li key={p.id} className="border-b border-[#E5E3DE] pb-0.5">
                      {p.email} · {p.role_pick} · {p.silo_count} silos
                      {p.byok && ` · byok: ${p.byok.join(',')}`}
                      {p.referrer_email && ` · referred by ${p.referrer_email.split('@')[0]}`}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>

        {/* 4th button: Stack on top (preserves existing users + credits real referrers) */}
        <div className="bg-[#FDFCF8] border-2 border-[#3A6B6A] rounded-lg p-4" data-testid="stack-card">
          <div className="flex items-start gap-2 mb-2">
            <Sparkle size={20} weight="duotone" className="text-[#3A6B6A] shrink-0 mt-0.5" />
            <div>
              <div className="font-serif-display text-base text-[#1C1B1A]">🧬 Stack on top</div>
              <div className="text-[11px] text-[#6B6A68] mt-0.5">
                Adds N randomized personas WITHOUT wiping. Picks real existing users
                as referrers and credits their upline with proper {monthly}/mo residuals
                + {gate}-gate rev-share. Each run STACKS — re-runs grow the base.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <label className="text-[11px] text-[#6B6A68]">Count:</label>
            <FreeNumberInput
              min={1} max={20}
              value={stackCount}
              onChange={(v) => setStackCount(v)}
              data-testid="stack-count-input"
              className="w-14 px-2 py-1 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#3A6B6A] tabular-nums"
            />
          </div>
          <label className="inline-flex items-center gap-1.5 text-[10px] text-[#6B6A68] mt-2 cursor-pointer" data-testid="stack-real-only-toggle">
            <input
              type="checkbox"
              checked={stackRealOnly}
              onChange={(e) => setStackRealOnly(e.target.checked)}
              className="w-3 h-3"
            />
            <span>Real users only (exclude test seeds — flip on for live mode)</span>
          </label>
          <button
            onClick={() => setStackOpen(true)}
            data-testid="stack-open-btn"
            className="mt-2 text-xs px-3 py-2 rounded bg-[#3A6B6A] text-white hover:bg-[#2C5453] flex items-center gap-1.5 font-medium"
          >
            <Sparkle size={12} weight="fill" /> Stack on top
          </button>
          {lastStack && (
            <div className="mt-3 text-[10px] bg-white border hairline rounded p-2">
              <div className="flex items-center gap-1 text-[#2F6B3A]"><CheckCircle size={10} weight="duotone" /> Last stack run:</div>
              <div className="text-[#6B6A68] mt-0.5">
                {lastStack.count} new personas · pool: {lastStack.referrer_pool_size} · credited
                <strong className="text-[#2F6B3A]"> ${(lastStack.earnings_summary?.total_cents || 0) / 100}</strong>
                {' '}to {lastStack.earnings_summary?.uplines_credited?.length || 0} uplines
              </div>
              <details className="mt-1.5">
                <summary className="cursor-pointer text-[9px] text-[#3A6B6A] hover:underline">Show earnings + roster</summary>
                <ul className="mt-1 space-y-0.5 text-[9px] font-mono text-[#1C1B1A] max-h-40 overflow-y-auto">
                  {lastStack.earnings_credited?.map((e, i) => (
                    <li key={i} className="border-b border-[#E5E3DE] pb-0.5 text-[#3A6B6A]">
                      💰 {e.to.split('@')[0]} ← {e.kind} ${e.amount_cents / 100} (from {e.from_user.split('@')[0]})
                    </li>
                  ))}
                  {lastStack.personas?.map((p) => (
                    <li key={p.id} className="border-b border-[#E5E3DE] pb-0.5">
                      {p.email} · {p.role_pick} · {p.silo_count} silos
                      {p.byok && ` · byok: ${p.byok.join(',')}`}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>

      {nukeOpen && (
        <ConfirmModal
          title="Nuke all data"
          subtitle="This wipes EVERY non-founder user, every other silo, all payment txns, the entire voucher ledger (credit lots, audit trail, review queue), affiliate + DFY money rows, all usage logs, and resets every voucher + the Platform Usage Reservoir to $0. Your account, your silos, your bookmarks + custom commands and your battle history survive (Deep clean also takes the bookmarks and commands)."
          phrase="NUKE-CONFIRMED"
          confirmLabel={deepClean ? "Initiate Deep Clean Nuke" : "Initiate Nuke"}
          accent="#C16E5A"
          onClose={() => setNukeOpen(false)}
          extraControl={(
            <div className="space-y-2 mt-3">
              <label className="flex items-start gap-2 text-[11px] text-[#1C1B1A] bg-white border-2 border-[#C16E5A] rounded-md px-3 py-2 cursor-pointer" data-testid="deep-clean-toggle">
                <input
                  type="checkbox"
                  checked={deepClean}
                  onChange={(e) => setDeepClean(e.target.checked)}
                  className="w-3.5 h-3.5 mt-0.5 shrink-0"
                  data-testid="deep-clean-checkbox"
                />
                <span className="leading-snug">
                  <strong className="text-[#7A3A2A]">🧹 Deep clean (also wipe my history)</strong>
                  <br />
                  <span className="text-[#6B6A68]">
                    Also clears your usage logs, audience counters, referral records,
                    event logs, buddy chats, bookmarks, custom commands, chat sessions,
                    workbench/boost/refinery jobs, and your own admin + go-live audit trail.
                    <br />
                    <em>Always preserved:</em> your silos, silo content, battle history,
                    push notifications + pre-built questions, YouTube libraries, and the
                    record of this nuke itself.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-[11px] text-[#1C1B1A] bg-white border-2 border-[#C16E5A] rounded-md px-3 py-2 cursor-pointer" data-testid="refund-stripe-sandbox-toggle">
                <input
                  type="checkbox"
                  checked={refundStripeSandbox}
                  onChange={(e) => setRefundStripeSandbox(e.target.checked)}
                  className="w-3.5 h-3.5 mt-0.5 shrink-0"
                  data-testid="refund-stripe-sandbox-checkbox"
                />
                <span className="leading-snug">
                  <strong className="text-[#7A3A2A]">💸 Full Stripe sandbox autopilot</strong>
                  <br />
                  <span className="text-[#6B6A68]">
                    Runs four Stripe cleanup steps automatically:
                    (1) cancels every live subscription so nothing renews and refills the ledger,
                    (2) refunds every test charge + cancels open PaymentIntents,
                    (3) purges every seeded test Customer / Subscription (the Stack-on-top leftovers too),
                    (4) covers the residual processing-fee burn so the live balance lands at ~$0.
                    <br />
                    <em>Sandbox-only:</em> auto-refuses if a live Stripe key is detected. Zero clicks needed after this — no manual "Cover residual fees" follow-up.
                  </span>
                </span>
              </label>
            </div>
          )}
          onConfirm={async () => {
            const r = await api.post("/admin/danger/nuke-all", {
              confirmation: "NUKE-CONFIRMED",
              reset_reservoir: true,
              deep_clean: deepClean,
              refund_stripe_sandbox: refundStripeSandbox,
              note: `Manual nuke via Super Admin at ${new Date().toISOString()}${deepClean ? " (deep clean)" : ""}${refundStripeSandbox ? " (stripe refund)" : ""}`,
            });
            setLastNuke(r.data.audit);
            const dcMsg = deepClean
              ? ` + deep cleaned ${Object.values(r.data.audit.deep_clean_counts || {}).reduce((a, b) => a + Math.max(0, b), 0)} founder rows`
              : "";
            const sr = r.data.audit.stripe_sandbox_refund || {};
            const sp = r.data.audit.stripe_artifact_purge || {};
            const sc = r.data.audit.stripe_residual_cover || {};
            let srMsg = "";
            if (refundStripeSandbox) {
              if (sr.skipped_reason) {
                srMsg = ` (Stripe refund skipped: ${sr.skipped_reason})`;
              } else {
                const parts = [
                  `refunded ${sr.refunds_issued || 0} charges ($${((sr.refund_amount_cents || 0) / 100).toFixed(2)})`,
                  `canceled ${sr.intents_canceled || 0} intents`,
                ];
                if (sp.customers_deleted) parts.push(`purged ${sp.customers_deleted} test customers`);
                if (sc.charge_cents) parts.push(`covered $${(sc.deficit_cents / 100).toFixed(2)} fee residue`);
                srMsg = ` + ${parts.join(" + ")}`;
              }
            }
            toast.success(`Nuke complete — wiped ${r.data.audit.wiped.user_ids_count} users + ${r.data.audit.wiped.silo_ids_count} silos${dcMsg}${srMsg}. Refreshing…`);
            setDeepClean(false);
            setRefundStripeSandbox(false);
            setTimeout(() => window.location.reload(), 3000);
          }}
        />
      )}

      {seedOpen && (
        <ConfirmModal
          title="Seed 6 test personas"
          subtitle="Creates fan, paid fan, red creator ($5 voucher), green creator (with your BYOK cloned), mixed creator, and affiliate creator. Each gets a starter silo + doc. Idempotent — re-runs purge any prior seeds first."
          phrase="SEED-TEST-ACCOUNTS"
          confirmLabel={stripeBackedStandard ? "Seed (3 Stripe-backed)" : "Seed personas"}
          accent="#4A5D4E"
          onClose={() => setSeedOpen(false)}
          extraControl={<StripeBackedCheckbox checked={stripeBackedStandard} onChange={setStripeBackedStandard} optionLabel="Creator Pro + green + red" accent="#4A5D4E" />}
          onConfirm={async () => {
            const r = await api.post("/admin/danger/seed-test-accounts", {
              confirmation: "SEED-TEST-ACCOUNTS",
              clone_founder_byok: true,
              populate_usage_history: populateUsage,
              stripe_backed: stripeBackedStandard,
            });
            setLastSeed(r.data);
            const sbMsg = r.data.stripe_backed_count ? ` (${r.data.stripe_backed_count} live in Stripe Sandbox)` : "";
            toast.success(`Seeded ${r.data.created.length} personas${sbMsg}. Password: ${r.data.password_for_all}. Refreshing…`);
            setStripeBackedStandard(false);
            setTimeout(() => window.location.reload(), 2500);
          }}
        />
      )}

      {randomOpen && (
        <ConfirmModal
          title={`🎲 Roll ${stripeBackedRandom ? Math.min(randomCount, 3) : randomCount} random personas`}
          subtitle={`Generates ${stripeBackedRandom ? Math.min(randomCount, 3) : randomCount} fully-randomized users with varied roles, BYOK loadouts, voucher levels, silo counts, payment states, ages, and a referral chain between them. Re-running purges prior random-test.app users first.${stripeBackedRandom ? " ✨ Stripe Sandbox-backed: capped at 3, each gets a real Stripe Customer + Subscription." : ""}`}
          phrase="SEED-RANDOM-PERSONAS"
          confirmLabel={stripeBackedRandom ? "Roll (3 Stripe-backed)" : "Roll the dice"}
          accent="#7B7AA8"
          onClose={() => setRandomOpen(false)}
          extraControl={<StripeBackedCheckbox checked={stripeBackedRandom} onChange={setStripeBackedRandom} optionLabel="all 3 random paid personas" accent="#7B7AA8" />}
          onConfirm={async () => {
            const r = await api.post("/admin/danger/seed-random-personas", {
              confirmation: "SEED-RANDOM-PERSONAS",
              count: randomCount,
              clone_founder_byok: true,
              populate_usage_history: populateUsage,
              stripe_backed: stripeBackedRandom,
            });
            setLastRandom(r.data);
            const sbMsg = r.data.stripe_backed_count ? ` (${r.data.stripe_backed_count} live in Stripe Sandbox)` : "";
            toast.success(`Rolled ${r.data.count} personas${sbMsg}. Password: ${r.data.password_for_all}. Refreshing…`);
            setStripeBackedRandom(false);
            setTimeout(() => window.location.reload(), 2500);
          }}
        />
      )}

      {stackOpen && (
        <ConfirmModal
          title={`🧬 Stack ${stripeBackedStack ? Math.min(stackCount, 3) : stackCount} on top of existing base`}
          subtitle={`Generates ${stripeBackedStack ? Math.min(stackCount, 3) : stackCount} randomized personas WITHOUT wiping anything. Each one picks a real existing user as referrer and properly credits their upline with ${monthly}/mo residuals (paid creators) or ${gate}-gate bonus rev-share (affiliates). Stacking — multiple runs grow the base.${stripeBackedStack ? " ✨ Stripe Sandbox-backed: capped at 3 paid personas, each generates real Stripe records visible in Stripe Dashboard. Killer combo with 6-month backdating — your upline accumulates 6 months of synthetic residuals from a real Stripe customer." : ""}`}
          phrase="SEED-ON-TOP"
          confirmLabel={stripeBackedStack ? "Stack (3 Stripe-backed)" : "Stack personas"}
          accent="#3A6B6A"
          onClose={() => setStackOpen(false)}
          extraControl={<StripeBackedCheckbox checked={stripeBackedStack} onChange={setStripeBackedStack} optionLabel="all 3 stacked paid personas" accent="#3A6B6A" highlight />}
          onConfirm={async () => {
            const r = await api.post("/admin/danger/seed-on-top-personas", {
              confirmation: "SEED-ON-TOP",
              count: stackCount,
              clone_founder_byok: true,
              populate_usage_history: populateUsage,
              referrer_pool: stackRealOnly ? "real_only" : "all_users",
              stripe_backed: stripeBackedStack,
            });
            setLastStack(r.data);
            const sbMsg = r.data.stripe_backed_count ? ` (${r.data.stripe_backed_count} live in Stripe Sandbox)` : "";
            toast.success(`Stacked ${r.data.count} personas${sbMsg}. $${(r.data.earnings_summary?.total_cents || 0) / 100} credited to ${r.data.earnings_summary?.uplines_credited?.length || 0} uplines. Refreshing…`);
            setStripeBackedStack(false);
            setTimeout(() => window.location.reload(), 2500);
          }}
        />
      )}
    </section>
  );
}

function StripeBackedCheckbox({ checked, onChange, optionLabel, accent, highlight }) {
  return (
    <label
      className="flex items-start gap-2 text-[11px] text-[#1C1B1A] rounded-md px-3 py-2 cursor-pointer mt-3 border-2 transition"
      style={{
        background: checked ? "#FFF7E8" : "white",
        borderColor: checked ? "#C58B3A" : accent,
        boxShadow: checked && highlight ? "0 0 0 3px rgba(197,139,58,0.15)" : "none",
      }}
      data-testid="stripe-backed-toggle"
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5 mt-0.5 shrink-0"
        data-testid="stripe-backed-checkbox"
      />
      <span className="leading-snug">
        <strong className="text-[#7A3A2A]">💳 Use real Stripe Sandbox (capped at 3)</strong>
        <br />
        <span className="text-[#6B6A68]">
          When checked, <strong>{optionLabel}</strong> are created via real{" "}
          <code className="bg-[#F4EDE0] px-1 rounded text-[10px]">stripe.Customer.create()</code> +{" "}
          <code className="bg-[#F4EDE0] px-1 rounded text-[10px]">stripe.Subscription.create()</code>{" "}
          with a test card attached. Real <code className="text-[10px]">cus_xxx</code> /{" "}
          <code className="text-[10px]">sub_xxx</code> IDs visible in your Stripe Sandbox Dashboard. Fires
          real <code className="text-[10px]">customer.subscription.created</code> +{" "}
          <code className="text-[10px]">invoice.payment_succeeded</code> webhooks. Adds ~5-10 seconds per
          persona.
        </span>
      </span>
    </label>
  );
}

function ConfirmModal({ title, subtitle, phrase, confirmLabel, accent, onClose, onConfirm, extraControl }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const match = typed === phrase;

  const handle = async () => {
    if (!match) return;
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="danger-confirm-modal"
    >
      <div
        className="modal-safe bg-[#FDFCF8] border-2 rounded-lg p-5 w-full max-w-md"
        style={{ borderColor: accent }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2 mb-3">
          <Warning size={20} weight="duotone" style={{ color: accent }} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-serif-display text-xl text-[#1C1B1A]">{title}</div>
            <div className="text-xs text-[#6B6A68] mt-1 leading-relaxed">{subtitle}</div>
          </div>
        </div>
        <div className="mt-4">
          <label className="text-[11px] text-[#6B6A68] block mb-1">
            Type <code className="bg-white px-1 rounded font-mono" style={{ color: accent }}>{phrase}</code> to confirm:
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            data-testid="danger-confirm-input"
            className="w-full px-2 py-1.5 border-2 rounded text-sm bg-white focus:outline-none font-mono"
            style={{ borderColor: match ? accent : "#E5E3DE" }}
            autoFocus
          />
        </div>
        {extraControl}
        <div className="flex gap-2 mt-4">
          <button
            onClick={handle}
            disabled={!match || busy}
            data-testid="danger-confirm-fire"
            className="flex-1 text-xs px-3 py-2 rounded text-white disabled:opacity-40 flex items-center justify-center gap-1.5"
            style={{ background: accent }}
          >
            {busy ? <Spinner size={11} className="animate-spin" /> : null}
            {busy ? "Working…" : confirmLabel}
          </button>
          <button
            onClick={onClose}
            data-testid="danger-confirm-cancel"
            className="text-xs px-3 py-2 rounded border hairline hover:border-[#6B6A68] flex items-center gap-1"
          >
            <XIcon size={11} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
